/* eslint-disable max-lines */
/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/refs */
/* eslint-disable max-lines-per-function, max-statements */

"use client"

import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react"
import mediaQueries from "styles/mediaQueries"
import motionTokens from "styles/motionTokens"

import Carousel from "./Carousel"
import type {
  CarouselShareButtonConfig,
  CarouselShareConfig,
  CarouselViewProps,
  CounterPlacement,
  CounterVariant
} from "./types"
export type {
  CarouselShareButtonConfig,
  CarouselShareConfig,
  CarouselShareDetail,
  CarouselSharePlacement,
  CounterPlacement,
  CounterVariant
} from "./types"
import type {
  CarouselOverlayState,
  OverlayInteraction
} from "./zoomOverlay.types"

export type CarouselInteraction =
  | "next"
  | "prev"
  | "thumbnail"
  | "next-boundary"
  | "prev-boundary"

export interface Props {
  children: ReactNode
  className?: string
  columnGap?: number
  counterPlacement?: CounterPlacement
  counterVariant?: CounterVariant
  id?: string
  isShowCounter?: boolean
  isShowCounterOnlyWhenMore?: boolean
  isShowCounterTop?: boolean
  isShowThumbnails?: boolean
  name?: string
  onNext?: () => void
  onPrev?: () => void

  onThumbnailPress?: (index: number) => void
  items?: unknown[]
  onInteraction?: (
    interaction: CarouselInteraction,
    detail: { index: number; item: unknown }
  ) => void
  slidesToScroll?: number
  slidesToShow?: number
  slidesToShowDesktop?: number
  slidesToShowMobile?: number
  slidesToShowTablet?: number
  thumbnails?: React.ReactNode[]
  enableHaptics?: boolean
  loop?: boolean
  zoomOverlay?: {
    name?: string
    openButtonLabel?: string
    onChangeIndex?: (index: number) => void
    onInteraction?: (interaction: OverlayInteraction, index: number) => void
  }
  share?: CarouselShareConfig
}

export interface ComponentProps extends CarouselViewProps {
  overlay?: CarouselOverlayState
}

const DEFAULT_ACTIVE_INDEX = 0
const DEFAULT_COLUMN_GAP = 10
const DEFAULT_SLIDES_TO_SCROLL = 1
const DEFAULT_SLIDES_TO_SHOW = 1
const DEFAULT_TRANSLATE_X = 0
const FIRST = 0
const HALF = 50
const TOTAL = 100
const TWO = 2

const HAPTIC_LIGHT = 15
const HAPTIC_MEDIUM = 12
const HAPTIC_BOUNDARY = 8
const HAPTIC_CLOSE = 10

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const useCarouselShareHelpers = (
  share: CarouselShareConfig | undefined,
  resolveItem: (index: number) => unknown
) =>
  useMemo(() => {
    if (!share) {
      return {
        getShareButtonProps: undefined,
        shareButtonPlacement: undefined
      } as const
    }

    const getShareButtonProps = (index: number) =>
      share.getButtonProps({ index, item: resolveItem(index) })

    return {
      getShareButtonProps,
      shareButtonPlacement: share.placement ?? "top-right"
    } as const
  }, [resolveItem, share])

type OverlayPropsOptions = {
  baseCarouselProps: CarouselViewProps
  columnGap: number
  getShareButtonProps?: (index: number) => CarouselShareButtonConfig | undefined
  isLooping: boolean
  slidesToShow: number
  syncZoomIndex: (index: number) => void
  zoomIndex: number
  zoomOverlay?: Props["zoomOverlay"]
  overlayThumbnailsContainerRef: RefObject<HTMLDivElement | null>
  overlayOnNext: () => void
  overlayOnPrev: () => void
}

const buildOverlayCarouselProps = ({
  baseCarouselProps,
  columnGap,
  getShareButtonProps,
  isLooping,
  slidesToShow,
  syncZoomIndex,
  zoomIndex,
  zoomOverlay,
  overlayThumbnailsContainerRef,
  overlayOnNext,
  overlayOnPrev
}: OverlayPropsOptions): CarouselViewProps | undefined => {
  if (!zoomOverlay) {
    return undefined
  }

  const totalSlides = baseCarouselProps.children.length
  const overlayMaxIndex = Math.max(0, totalSlides - 1)
  const overlayCanGoNext = isLooping || zoomIndex < overlayMaxIndex
  const overlayCanGoPrev = isLooping || zoomIndex > 0

  return {
    ...baseCarouselProps,
    activeIndex: zoomIndex,
    bounceDirection: baseCarouselProps.bounceDirection,
    canGoNext: overlayCanGoNext,
    canGoPrev: overlayCanGoPrev,
    className: [baseCarouselProps.className, "blublocks-carousel--overlay"]
      .filter(Boolean)
      .join(" ")
      .trim(),
    counterPlacement:
      baseCarouselProps.counterPlacement ?? baseCarouselProps.counterPlacement,
    isOverlayLayout: true,
    isShowCounterTop: false,
    onNext: overlayOnNext,
    onPrev: overlayOnPrev,
    onSelectIndex: (index: number) => syncZoomIndex(index),
    shareButtonProps: getShareButtonProps
      ? getShareButtonProps(zoomIndex)
      : undefined,
    thumbnailsContainerRef: overlayThumbnailsContainerRef,
    translateX: -zoomIndex * (TOTAL / Math.max(1, slidesToShow)),
    translateXOffset: columnGap * zoomIndex,
    zoomButtonHandler: undefined,
    zoomButtonLabel: undefined
  }
}

const usePrepareComponent = (props: Props): ComponentProps => {
  const id = useId()

  const [activeIndex, setActiveIndex] = useState(DEFAULT_ACTIVE_INDEX)
  const activeIndexRef = useRef(DEFAULT_ACTIVE_INDEX)
  const [translateX, setTranslateX] = useState(DEFAULT_TRANSLATE_X)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchMove, setTouchMove] = useState<number | null>(null)
  const [isZoomOpen, setIsZoomOpen] = useState(false)
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ACTIVE_INDEX)
  const [bounceDirection, setBounceDirection] = useState<
    "prev" | "next" | null
  >(null)

  const bounceTimeoutRef = useRef<number | null>(null)

  /**
   * Normalize children as an array
   */
  const children = useMemo(
    () => (Array.isArray(props.children) ? props.children : [props.children]),
    [props.children]
  )
  const totalSlides = children.length
  const { items, onInteraction } = props
  const enableHaptics = props.enableHaptics ?? false
  const isLooping = props.loop ?? true

  const resolveItem = useCallback(
    (index: number) => {
      if (Array.isArray(items)) {
        return items[index]
      }
      return children[index]
    },
    [children, items]
  )

  const emitInteraction = useCallback(
    (interaction: CarouselInteraction, index: number) => {
      if (!onInteraction) return
      const item = resolveItem(index)
      onInteraction(interaction, { index, item })
    },
    [onInteraction, resolveItem]
  )

  const { getShareButtonProps, shareButtonPlacement } = useCarouselShareHelpers(
    props.share,
    resolveItem
  )
  const activeShareButtonProps = getShareButtonProps
    ? getShareButtonProps(activeIndex)
    : undefined

  const vibrate = useCallback(
    (pattern: number | number[] = HAPTIC_LIGHT) => {
      if (!enableHaptics) return
      if (typeof navigator === "undefined") return
      if (typeof navigator.vibrate !== "function") return
      navigator.vibrate(pattern)
    },
    [enableHaptics]
  )

  const triggerBounce = useCallback((direction: "prev" | "next") => {
    if (bounceTimeoutRef.current) {
      window.clearTimeout(bounceTimeoutRef.current)
    }
    setBounceDirection(direction)
    bounceTimeoutRef.current = window.setTimeout(() => {
      setBounceDirection(null)
      bounceTimeoutRef.current = null
    }, motionTokens.duration.medium)
  }, [])

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const thumbnailsContainerRef = useRef<HTMLDivElement>(null)
  const overlayThumbnailsContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    activeIndexRef.current = activeIndex
  }, [activeIndex])

  /**
   * getSlidesToShow
   *
   * @description Determines the number of slides to show based on the current screen size
   * and the provided props
   * DO NOT USE OUTSIDE OF useEffect if we are using SSR
   * @returns {number} The number of slides to show
   *
   * */
  const getSlidesToShow = useCallback(() => {
    if (
      window.matchMedia(
        mediaQueries.desktop.replace("@media only screen and ", "")
      ).matches
    ) {
      return (
        props.slidesToShowDesktop ??
        props.slidesToShow ??
        DEFAULT_SLIDES_TO_SHOW
      )
    } else if (
      window.matchMedia(
        mediaQueries.tablet.replace("@media only screen and ", "")
      ).matches
    ) {
      return (
        props.slidesToShowTablet ?? props.slidesToShow ?? DEFAULT_SLIDES_TO_SHOW
      )
    }

    return (
      props.slidesToShowMobile ?? props.slidesToShow ?? DEFAULT_SLIDES_TO_SHOW
    )
  }, [
    props.slidesToShow,
    props.slidesToShowDesktop,
    props.slidesToShowMobile,
    props.slidesToShowTablet
  ])

  const [slidesToShow, setSlidesToShow] = useState(0)

  useEffect(() => {
    const onResize = () => {
      setSlidesToShow(getSlidesToShow())
    }

    onResize()
    window.addEventListener("resize", onResize)

    return () => window.removeEventListener("resize", onResize)
  }, [getSlidesToShow])

  const slidesToScroll = props.slidesToScroll ?? DEFAULT_SLIDES_TO_SCROLL
  const columnGap = props.columnGap ?? DEFAULT_COLUMN_GAP

  useEffect(() => {
    // Canonical movement: one slot = 100 / slidesToShow
    setTranslateX(-activeIndex * (TOTAL / Math.max(1, slidesToShow)))
  }, [activeIndex, slidesToShow])
  useEffect(() => {
    if (!props.zoomOverlay) return
    if (!isZoomOpen) {
      setZoomIndex(clamp(activeIndex, 0, Math.max(0, children.length - 1)))
    }
  }, [activeIndex, children.length, isZoomOpen, props.zoomOverlay])

  useEffect(() => {
    return () => {
      if (bounceTimeoutRef.current) {
        window.clearTimeout(bounceTimeoutRef.current)
      }
    }
  }, [])

  const updateFromInteraction = useCallback(
    (direction: "next" | "prev") => {
      const prevIndex = activeIndexRef.current
      const effectiveSlidesToShow = Math.max(slidesToShow, 1)
      const maxIndex = Math.max(0, children.length - effectiveSlidesToShow)

      let bounce: "prev" | "next" | null = null
      let vibratePattern: number | number[] = HAPTIC_LIGHT
      // eslint-disable-next-line no-useless-assignment
      let interaction: CarouselInteraction = direction
      let nextIndex = prevIndex

      if (direction === "next") {
        const reachedNextBoundary = !isLooping && prevIndex >= maxIndex
        if (reachedNextBoundary) {
          interaction = "next-boundary"
          bounce = "next"
          vibratePattern = HAPTIC_BOUNDARY
        } else {
          const candidate = Math.min(prevIndex + slidesToScroll, maxIndex)
          nextIndex = isLooping
            ? prevIndex >= maxIndex
              ? DEFAULT_ACTIVE_INDEX
              : candidate
            : candidate
          interaction = "next"
        }
      } else {
        const reachedPrevBoundary =
          !isLooping && prevIndex <= DEFAULT_ACTIVE_INDEX
        if (reachedPrevBoundary) {
          interaction = "prev-boundary"
          bounce = "prev"
          vibratePattern = HAPTIC_BOUNDARY
        } else {
          const candidate = Math.max(
            prevIndex - slidesToScroll,
            DEFAULT_ACTIVE_INDEX
          )
          nextIndex = isLooping
            ? prevIndex === DEFAULT_ACTIVE_INDEX
              ? Math.max(0, maxIndex)
              : candidate
            : candidate
          interaction = "prev"
        }
      }

      if (nextIndex !== prevIndex) {
        activeIndexRef.current = nextIndex
        setActiveIndex(nextIndex)
      } else {
        setActiveIndex(prevIndex)
      }

      const emittedIndex =
        interaction === "next-boundary" || interaction === "prev-boundary"
          ? clamp(prevIndex, 0, children.length - 1)
          : clamp(nextIndex, 0, children.length - 1)

      emitInteraction(interaction, emittedIndex)
      if (bounce) {
        triggerBounce(bounce)
      }
      vibrate(vibratePattern)
    },
    [
      children.length,
      emitInteraction,
      isLooping,
      slidesToScroll,
      slidesToShow,
      triggerBounce,
      vibrate
    ]
  )

  const onNext = useCallback(() => {
    props.onNext?.()
    updateFromInteraction("next")
  }, [props, updateFromInteraction])

  const onPrev = useCallback(() => {
    props.onPrev?.()
    updateFromInteraction("prev")
  }, [props, updateFromInteraction])

  const syncZoomIndex = useCallback(
    (nextIndex: number, interaction?: OverlayInteraction) => {
      if (!props.zoomOverlay || totalSlides === 0) return
      const normalized = clamp(nextIndex, 0, totalSlides - 1)
      setZoomIndex(normalized)
      if (!isZoomOpen) {
        setActiveIndex(normalized)
      }
      props.zoomOverlay.onChangeIndex?.(normalized)
      if (interaction) {
        props.zoomOverlay.onInteraction?.(interaction, normalized)
      }
    },
    [isZoomOpen, props.zoomOverlay, totalSlides]
  )

  const openZoom = useCallback(
    (index: number) => {
      if (!props.zoomOverlay || totalSlides === 0) return
      syncZoomIndex(index, "open")
      setIsZoomOpen(true)
      vibrate(HAPTIC_MEDIUM)
    },
    [props.zoomOverlay, syncZoomIndex, totalSlides, vibrate]
  )

  const closeZoom = useCallback(() => {
    if (props.zoomOverlay && totalSlides > 0) {
      const normalized = clamp(zoomIndex, 0, totalSlides - 1)
      props.zoomOverlay.onInteraction?.("close", normalized)
      setActiveIndex(normalized)
    }
    setIsZoomOpen(false)
    vibrate(HAPTIC_CLOSE)
  }, [props.zoomOverlay, totalSlides, vibrate, zoomIndex])

  const goToNextAsset = useCallback(() => {
    if (!props.zoomOverlay || totalSlides <= 1) return
    const maxIndex = Math.max(0, totalSlides - 1)

    if (!isLooping && zoomIndex >= maxIndex) {
      props.zoomOverlay.onInteraction?.("next-boundary", zoomIndex)
      triggerBounce("next")
      vibrate(HAPTIC_BOUNDARY)
      return
    }

    const nextIndex =
      isLooping && zoomIndex >= maxIndex ? 0 : Math.min(zoomIndex + 1, maxIndex)
    if (nextIndex === zoomIndex) return

    vibrate(HAPTIC_LIGHT)
    syncZoomIndex(nextIndex, "next")
  }, [
    isLooping,
    props.zoomOverlay,
    syncZoomIndex,
    totalSlides,
    triggerBounce,
    vibrate,
    zoomIndex
  ])

  const goToPreviousAsset = useCallback(() => {
    if (!props.zoomOverlay || totalSlides <= 1) return
    const maxIndex = Math.max(0, totalSlides - 1)

    if (!isLooping && zoomIndex <= 0) {
      props.zoomOverlay.onInteraction?.("prev-boundary", zoomIndex)
      triggerBounce("prev")
      vibrate(HAPTIC_BOUNDARY)
      return
    }

    const nextIndex =
      isLooping && zoomIndex <= 0 ? maxIndex : Math.max(zoomIndex - 1, 0)
    if (nextIndex === zoomIndex) return

    vibrate(HAPTIC_LIGHT)
    syncZoomIndex(nextIndex, "previous")
  }, [
    isLooping,
    props.zoomOverlay,
    syncZoomIndex,
    totalSlides,
    triggerBounce,
    vibrate,
    zoomIndex
  ])

  const overlayOnNext = useCallback(() => {
    goToNextAsset()
    props.onNext?.()
  }, [goToNextAsset, props])

  const overlayOnPrev = useCallback(() => {
    goToPreviousAsset()
    props.onPrev?.()
  }, [goToPreviousAsset, props])

  useEffect(() => {
    if (!isZoomOpen) return
    closeButtonRef.current?.focus()
  }, [isZoomOpen])

  useEffect(() => {
    if (!isZoomOpen) return undefined

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        closeZoom()
        return
      }

      if (event.key === "ArrowRight") {
        event.preventDefault()
        goToNextAsset()
        return
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault()
        goToPreviousAsset()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [closeZoom, goToNextAsset, goToPreviousAsset, isZoomOpen])

  const zoomButtonHandler = useMemo(() => {
    if (!props.zoomOverlay) return undefined

    return () => openZoom(activeIndex)
  }, [activeIndex, openZoom, props.zoomOverlay])

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    setTouchStart(event.touches[FIRST].clientX)
  }

  const onTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    setTouchMove(event.touches[FIRST].clientX)
  }

  const onTouchEnd = () => {
    if (touchStart && touchMove) {
      const swipeDistance = touchMove - touchStart
      if (swipeDistance > HALF) {
        onPrev()
      } else if (swipeDistance < -HALF) {
        onNext()
      }
    }
    setTouchStart(null)
    setTouchMove(null)
  }

  const isShowArrows = useMemo(
    () => children.length > slidesToShow,
    [children.length, slidesToShow]
  )

  const isShowCounter = useMemo(() => {
    if (props.isShowCounter) return true
    if (props.isShowCounterOnlyWhenMore) return children.length > slidesToShow
    return false
  }, [
    children.length,
    props.isShowCounter,
    props.isShowCounterOnlyWhenMore,
    slidesToShow
  ])

  const translateXOffset = useMemo(
    () => columnGap * (activeIndex + 0),
    [activeIndex, columnGap]
  )

  // Thumbnails normalization
  const thumbnails = useMemo(
    () =>
      Array.isArray(props.thumbnails)
        ? props.thumbnails
        : props.thumbnails
          ? [props.thumbnails]
          : [],
    [props.thumbnails]
  )

  const isShowThumbnails = useMemo(() => {
    if (props.isShowThumbnails === false) return false
    return thumbnails.length > 0
  }, [props.isShowThumbnails, thumbnails.length])

  const onSelectIndex = useCallback(
    (index: number) => {
      const previousIndex = activeIndexRef.current
      const clamped = clamp(index, 0, Math.max(0, children.length - 1))
      const effectiveSlidesToShow = Math.max(slidesToShow, 1)
      const maxIndex = Math.max(0, children.length - effectiveSlidesToShow)
      const nextIndex = Math.min(clamped, maxIndex)
      const emittedIndex = clamp(nextIndex, 0, children.length - 1)

      if (nextIndex === previousIndex) return

      activeIndexRef.current = nextIndex
      setActiveIndex(nextIndex)
      emitInteraction("thumbnail", emittedIndex)
      vibrate(6)
    },
    [children.length, emitInteraction, slidesToShow, vibrate]
  )

  const effectiveSlidesToShow = Math.max(slidesToShow, 1)
  const maxIndex = Math.max(0, children.length - effectiveSlidesToShow)
  const canGoNext = isLooping || activeIndex < maxIndex
  const canGoPrev = isLooping || activeIndex > DEFAULT_ACTIVE_INDEX

  const baseCarouselProps: CarouselViewProps = {
    activeIndex,
    bounceDirection,
    children,
    className: props.className,
    columnGap,
    canGoNext,
    canGoPrev,
    counterPlacement:
      props.counterPlacement ??
      (props.isShowCounterTop ? "top-right" : "bottom-right"),
    counterVariant: props.counterVariant ?? "total",
    id: props.id ?? id,
    isShowArrows,
    isShowCounter,
    isShowCounterTop: props.zoomOverlay ? false : props.isShowCounterTop,
    isShowThumbnails,
    name: props.name,
    onNext,
    onPrev,
    onSelectIndex,
    onThumbnailPress: props.onThumbnailPress,
    onTouchEnd,
    onTouchMove,
    onTouchStart,
    slidesToShow,
    shareButtonProps: activeShareButtonProps,
    shareButtonPlacement,
    thumbnails,
    thumbnailsContainerRef,
    translateX,
    translateXOffset,
    zoomButtonHandler,
    zoomButtonLabel: props.zoomOverlay
      ? (props.zoomOverlay.openButtonLabel ?? "Open fullscreen zoom")
      : undefined
  }

  const overlayCarouselProps = buildOverlayCarouselProps({
    baseCarouselProps,
    columnGap,
    getShareButtonProps,
    isLooping,
    slidesToShow,
    syncZoomIndex,
    zoomIndex,
    zoomOverlay: props.zoomOverlay,
    overlayThumbnailsContainerRef,
    overlayOnNext,
    overlayOnPrev
  })

  const overlay: CarouselOverlayState | undefined = overlayCarouselProps
    ? {
        carouselProps: overlayCarouselProps,
        closeButtonRef,
        isOpen: isZoomOpen,
        name: props.zoomOverlay?.name ?? props.name,
        onClose: closeZoom
      }
    : undefined

  // Clamp scrolling on the container (not UL) to avoid right-gap when centering
  useEffect(() => {
    if (!isShowThumbnails) return

    const updateScroll = (container: HTMLDivElement | null) => {
      if (!container) return
      const el = container.querySelector(
        `[data-thumb-index="${activeIndex}"]`
      ) as HTMLElement | null
      if (!el) return

      const targetCenter = el.offsetLeft + el.offsetWidth / TWO
      const desired = targetCenter - container.clientWidth / TWO
      const maxScroll = container.scrollWidth - container.clientWidth
      const nextLeft = Math.min(Math.max(desired, 0), Math.max(0, maxScroll))

      container.scrollTo({ behavior: "smooth", left: nextLeft })
    }

    updateScroll(thumbnailsContainerRef.current)
    updateScroll(overlayThumbnailsContainerRef.current)
  }, [activeIndex, isShowThumbnails])

  return {
    ...baseCarouselProps,
    overlay
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Carousel
)
